import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import clsx from 'clsx';
import { formatMonth } from 'PDF/helpers/formatMonthPDF';

import { CardsFlexContainer, navSections, useRegisterSection } from 'components/OnePageTemplates/common';
import { useDataContext } from 'components/OnePageTemplates/context';
import { getFormattedNumber } from 'components/OnePageTemplates/helpers/getFormattedNumber';
import { IInfoCard } from 'components/OnePageTemplates/OnePageFirstStyle/components/InfoCard/InfoCard';

import { InfoCard } from '../components/InfoCard';

import styles from './FinancialProjectionsSection.module.scss';

export const FinancialProjectionsSection: React.FC = () => {
  const { isExtendedPreview, data } = useDataContext();
  const { t } = useTranslation();
  const registerNavSection = useRegisterSection(navSections.financialProjections);

  const cardsToRender: IInfoCard[] = [
    data.projectedAnnualIncome &&
      data.projectedAnnualIncome !== '0' && {
        label: t('onePageMapping.projectedAnnualIncome'),
        value: getFormattedNumber({ data: data.projectedAnnualIncome, startSymbol: '$' }),
      },
    data.projectedAnnualProfit &&
      data.projectedAnnualProfit !== '0' && {
        label: t('onePageMapping.projectedAnnualProfit'),
        value: getFormattedNumber({ data: data.projectedAnnualProfit, startSymbol: '$' }),
      },
    data.forecastedProfitability &&
      data.forecastedProfitability !== '0%' && {
        label: t('onePageMapping.projectedProfitability'),
        value: data.forecastedProfitability,
      },
    data.projectPaybackPeriod &&
      data.projectPaybackPeriod !== '0 months' && {
        label: t('onePageMapping.projectedPaybackPeriod'),
        value: formatMonth(data.projectPaybackPeriod),
      },
    isExtendedPreview &&
      data.returnOnInvestment &&
      data.returnOnInvestment !== '0' && {
        label: t('onePageMapping.returnOfInvest'),
        value: getFormattedNumber({ data: data.returnOnInvestment, endSymbol: '%' }),
      },
    isExtendedPreview &&
      data.internalRateOfReturn &&
      data.internalRateOfReturn !== '0' && {
        label: t('onePageMapping.internalRateReturn'),
        value: getFormattedNumber({ data: data.internalRateOfReturn, endSymbol: '%' }),
      },
  ].filter(Boolean) as IInfoCard[];

  if (!cardsToRender.length) {
    return null;
  }

  return (
    <Box {...registerNavSection} className={clsx(styles.section, styles.financialProjections)}>
      <Box className={styles.wrapper}>
        <Box className={styles.financialProjectionsTitle}>{t('chapters.FINANCIAL_PROJECTIONS')}</Box>
        <Box className={styles.infoCardsWrapper}>
          <CardsFlexContainer
            containerStyles={clsx(styles.cardsContainer, { [styles.containerWithOneCard]: cardsToRender.length === 1 })}
          >
            {cardsToRender.slice(0, cardsToRender.length > 4 ? 3 : undefined).map((card) => (
              <InfoCard variant="blur" key={card.label} label={card.label} value={card.value} />
            ))}
          </CardsFlexContainer>
          {cardsToRender.length > 4 && (
            <CardsFlexContainer containerStyles={styles.cardsContainer}>
              {cardsToRender.slice(3).map((card) => (
                <InfoCard variant="blur" key={card.label} label={card.label} value={card.value} />
              ))}
            </CardsFlexContainer>
          )}
        </Box>
      </Box>
    </Box>
  );
};
